import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

import { optionalClassName } from '../../internal/optionalClassName.ts';

/**
 * The panel an account dashboard opens with: who is signed in, the way out, and
 * the brand's own decoration behind both.
 *
 *   <AccountHero style={{ background: tenantGradient }}>
 *     <AccountHeroBody>
 *       <AccountHeroIdentity>
 *         {fullName === null ? null : <AccountHeroName>{fullName}</AccountHeroName>}
 *       </AccountHeroIdentity>
 *       <AccountHeroActions>
 *         <AccountLogoutButton variant={AccountLogoutButtonVariant.INVERTED} … />
 *       </AccountHeroActions>
 *     </AccountHeroBody>
 *     <AccountHeroDecoration variant={AccountHeroDecorationVariant.TUCKED_LOWER_LEFT}>
 *       <IconSignal className="text-primary/5" />
 *     </AccountHeroDecoration>
 *   </AccountHero>
 *
 * **The ground is the page's, not the part's.** What paints behind the panel is
 * a per-instance value — a tenant gradient today, a CMS-authored one tomorrow —
 * so it rides the spread native `style` prop as a final CSS value computed
 * app-side (spec § "Per-instance CMS styling"). No shared file here names a
 * variable a tenant owns.
 *
 * **The identity place is kept whether or not a name resolves.** A shopper whose
 * name the backend has not filled in leaves `AccountHeroIdentity` empty rather
 * than absent: the body distributes its two places with `justify-between`, and a
 * lone survivor would take the top and drag the way out up with it.
 *
 * **The decoration is the part's own structure**, not something a composition
 * has to remember: it hangs off the panel's corner at a size the panel decides,
 * and only the artwork inside it is the brand's.
 *
 * Server-first: every part here is a plain component.
 */

/**
 * The panel itself: a fixed-height band the decoration is clipped to, painting
 * its contents in the inverted foreground its dark ground calls for.
 */
const accountHeroClasses =
  'text-foreground-inverted lap:px-20 lap:py-14 relative flex h-[366px] w-full flex-col justify-between overflow-hidden rounded-lg px-5 py-5';

export type AccountHeroProps = ComponentProps<'div'>;

/** The dashboard's opening panel. */
export const AccountHero = ({ className, ...props }: AccountHeroProps) => (
  <div data-slot="account-hero" className={clsxm(accountHeroClasses, className)} {...props} />
);

/** Who is signed in beside the way out — stacked while narrow, side by side once wide. */
const accountHeroBodyClasses = 'lap:flex-row flex flex-1 flex-col justify-between';

export type AccountHeroBodyProps = ComponentProps<'div'>;

/** The panel's content row. */
export const AccountHeroBody = ({ className, ...props }: AccountHeroBodyProps) => (
  <div
    data-slot="account-hero-body"
    className={clsxm(accountHeroBodyClasses, className)}
    {...props}
  />
);

export type AccountHeroIdentityProps = ComponentProps<'div'>;

/**
 * Where the signed-in shopper is named.
 *
 * It paints nothing, so it declares no recipe — it is the place, and the place
 * is what the part is for: it holds the top of the panel open when there is no
 * name to put in it.
 */
export const AccountHeroIdentity = ({ className, ...props }: AccountHeroIdentityProps) => (
  <div
    data-slot="account-hero-identity"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

/** The name itself, set in the brand's display face at the panel's largest size. */
const accountHeroNameClasses = 'font-heading lap:text-4xl/[50px] text-[26px]/10';

export type AccountHeroNameProps = ComponentProps<'div'>;

/**
 * The signed-in shopper's name.
 *
 * A neutral element rather than a heading: a page wanting this text in its own
 * outline composes `@vs-shop/ui/heading` around it, where the level is visible.
 */
export const AccountHeroName = ({ className, ...props }: AccountHeroNameProps) => (
  <div
    data-slot="account-hero-name"
    className={clsxm(accountHeroNameClasses, className)}
    {...props}
  />
);

/** The way out of the account: the panel's full measure while narrow, its own once wide. */
const accountHeroActionsClasses = 'lap:w-auto w-full';

export type AccountHeroActionsProps = ComponentProps<'div'>;

/** The panel's action place. */
export const AccountHeroActions = ({ className, ...props }: AccountHeroActionsProps) => (
  <div
    data-slot="account-hero-actions"
    className={clsxm(accountHeroActionsClasses, className)}
    {...props}
  />
);

/**
 * The frame the brand's artwork hangs in, hung off a corner of whatever box
 * carries it and clipped by that box.
 *
 * `--size-signal` is the frame's own measure and nothing else reads it: the
 * artwork is square, and where it hangs is stated as a fraction of how big it
 * is, so one number moves the whole decoration at each width.
 *
 * The appearance axis is which corner it hangs off and how far past it — the
 * only thing that differs between the surfaces that carry one. The account
 * panel tucks a disc INSIDE its lower-left corner; a footer hangs two of them
 * half outside its own edges, one at the top and one at the bottom right, and
 * the top one crosses to the other side once there is room for the copy it
 * would otherwise sit behind.
 */
export const accountHeroDecorationVariants = cva(
  'pointer-events-none absolute size-[var(--size-signal)]',
  {
    variants: {
      variant: {
        /** Tucked inside the lower-left corner, a third of its own size in. */
        tuckedLowerLeft:
          'lap:[--size-signal:700px] lap:top-[calc(var(--size-signal)/6)] bottom-[calc(var(--size-signal)/3)] -left-[calc(var(--size-signal)/3)] [--size-signal:400px]',
        /** Half outside the top corner — trailing while narrow, leading once wide. */
        bleedingTop:
          'lap:[--size-signal:600px] lap:-left-[calc(var(--size-signal)/2)] -top-[calc(var(--size-signal)/2)] -right-[calc(var(--size-signal)/2)] [--size-signal:400px]',
        /**
         * Hung off the upper-right corner — an eighth of its own size above it
         * and a third of it past the edge, so the arc it draws crosses the
         * corner rather than sitting in it.
         */
        bleedingUpperRight:
          '-top-[calc(var(--size-signal)/8)] -right-[calc(var(--size-signal)/3)] [--size-signal:530px]',
        /** Half outside the lower-right corner, at one size throughout. */
        bleedingLowerRight:
          '-right-[calc(var(--size-signal)/2)] -bottom-[calc(var(--size-signal)/2)] [--size-signal:600px]',
        /**
         * Half outside the lower-LEFT corner, the mirror of the arm above it —
         * a band that hangs one disc off each of two opposite corners needs the
         * pair to be the same distance out.
         */
        bleedingLowerLeft:
          '-bottom-[calc(var(--size-signal)/2)] -left-[calc(var(--size-signal)/2)] [--size-signal:600px]',
        /**
         * Half outside the upper-right corner, at the same size and the same
         * distance as the two above. Distinct from `bleedingUpperRight`, which
         * hangs an eighth of its size above the corner and a third past it: that
         * one crosses the corner, this one sits half outside it.
         */
        bleedingUpperRightHalf:
          '-top-[calc(var(--size-signal)/2)] -right-[calc(var(--size-signal)/2)] [--size-signal:600px]',
        /**
         * Hung off the upper-LEFT corner, half its own size above it and a
         * third of it past the edge — and the one arm that grows with the
         * viewport rather than standing at one size, because the surface that
         * carries it is a full-width band rather than a panel.
         */
        bleedingUpperLeftGrowing:
          'tab:[--size-signal:500px] lap:[--size-signal:700px] -top-[calc(var(--size-signal)/2)] -left-[calc(var(--size-signal)/3)] [--size-signal:300px]',
        /** The opt-out: a tenant hanging the frame itself. */
        unstyled: '',
      },
    },
  }
);

type AccountHeroDecorationVariantName = NonNullable<
  VariantProps<typeof accountHeroDecorationVariants>['variant']
>;

export type AccountHeroDecorationVariant = AccountHeroDecorationVariantName;

/**
 * The decoration's appearance axis, derived from the recipe and bound to it by
 * `satisfies`, so the union and the recipe cannot drift.
 */
export const AccountHeroDecorationVariant = {
  /** Tucked inside the lower-left corner, a third of its own size in. */
  TUCKED_LOWER_LEFT: 'tuckedLowerLeft',
  /** Half outside the top corner — trailing while narrow, leading once wide. */
  BLEEDING_TOP: 'bleedingTop',
  /** Hung off the upper-right corner, crossing it rather than sitting in it. */
  BLEEDING_UPPER_RIGHT: 'bleedingUpperRight',
  /** Half outside the lower-right corner, at one size throughout. */
  BLEEDING_LOWER_RIGHT: 'bleedingLowerRight',
  /** Half outside the lower-left corner — the mirror of the one above. */
  BLEEDING_LOWER_LEFT: 'bleedingLowerLeft',
  /** Half outside the upper-right corner, rather than crossing it. */
  BLEEDING_UPPER_RIGHT_HALF: 'bleedingUpperRightHalf',
  /** Hung off the upper-left corner, growing with the viewport. */
  BLEEDING_UPPER_LEFT_GROWING: 'bleedingUpperLeftGrowing',
  /** The opt-out: a tenant hanging the frame itself. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, AccountHeroDecorationVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type AccountHeroDecorationProps = ComponentProps<'div'> & {
  readonly variant: AccountHeroDecorationVariant;
};

/**
 * The decoration's frame.
 *
 * `aria-hidden`, because it is ornament: it carries no information a shopper
 * who cannot see it is missing. `pointer-events-none` is structural and outside
 * the axis — the disc is an absolute box of several hundred pixels painted over
 * its siblings, and an arm that forgot it would swallow every control beneath
 * it silently (`tools/guards/signalDecoration.unit.test.ts`).
 */
export const AccountHeroDecoration = ({
  className,
  variant,
  ...props
}: AccountHeroDecorationProps) => (
  <div
    aria-hidden="true"
    data-slot="signal-decoration"
    className={clsxm(accountHeroDecorationVariants({ variant }), className)}
    {...props}
  />
);
